import React, { FC } from "react";
import { Box, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";

import { getTraitGroups } from "../../../../helpers";

interface TraitSettingsTabProps {
  traitMaxValues: Record<string, number>;
  setTraitMaxValues: React.Dispatch<React.SetStateAction<Record<string, number>>>;
  parentTotals: Record<string, number>;
}

export const TraitSettingsTab: FC<TraitSettingsTabProps> = ({
  traitMaxValues,
  setTraitMaxValues,
  parentTotals,
}) => {
  const { t } = useTranslation();
  const traitGroups = getTraitGroups(t);

  return (
    <Box display="flex" flexDirection="column" gap={1}>
      <Box
        sx={{
          border: "1px solid",
          borderColor: "grey.200",
          borderRadius: 1,
          maxHeight: 320,
          overflowY: "auto",
        }}
      >
        <Box
          display="grid"
          gridTemplateColumns="2fr 1.5fr 1fr"
          bgcolor="grey.100"
          px={1}
          py={0.5}
          fontWeight={600}
          fontSize="0.85rem"
          sx={{ position: "sticky", top: 0, zIndex: 2 }}
        >
          <span>{t("paygap.gradeEstimator.traitHeader.name")}</span>
          <span>{t("paygap.gradeEstimator.traitHeader.desc")}</span>
          <span>{t("paygap.gradeEstimator.traitHeader.limit")}</span>
        </Box>
        {traitGroups.map((group, gIdx) => (
          <React.Fragment key={`group-${group.parent.id}`}>
            <Box
              display="grid"
              gridTemplateColumns="2fr 1.5fr 1fr"
              px={1}
              py={0.75}
              sx={{
                borderTop: "1px solid",
                borderColor: "grey.100",
                backgroundColor: gIdx % 2 === 0 ? "white" : "grey.50",
                fontWeight: 600,
              }}
            >
              <span>{group.parent.name}</span>
              <span>{group.parent.desc}</span>
              <TextField
                variant="filled"
                size="small"
                type="number"
                inputProps={{ min: 0, max: 999, readOnly: true }}
                value={parentTotals[group.parent.id] ?? 0}
                disabled
              />
            </Box>
            {group.children.map((child, idx) => (
              <Box
                key={child.id}
                display="grid"
                gridTemplateColumns="2fr 1.5fr 1fr"
                px={1}
                py={0.65}
                sx={{
                  borderTop: "1px solid",
                  borderColor: "grey.100",
                  backgroundColor: (gIdx + idx) % 2 === 0 ? "white" : "grey.50",
                  fontSize: "0.9rem",
                }}
              >
                <span>{child.name}</span>
                <span>{child.desc}</span>
                <TextField
                  size="small"
                  type="number"
                  variant="filled"
                  value={traitMaxValues[child.id] ?? child.max}
                  onWheel={(e): void => (e.target as HTMLInputElement).blur()}
                  onChange={(e): void => {
                    const val = e.target.value === "" ? 0 : Number(e.target.value);

                    setTraitMaxValues((prev) => {
                      const next = { ...prev, [child.id]: val };

                      next[group.parent.id] = group.children.reduce(
                        (sum, c) =>
                          sum + (c.id === child.id ? val : next[c.id] || 0),
                        0,
                      );

                      return next;
                    });
                  }}
                />
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
    </Box>
  );
};
